import React, { useId } from 'react';
import { clsx } from 'clsx';

export interface FormFieldProps {
  /** Label text */
  label: string;
  /** Error message */
  error?: string;
  /** Show required asterisk */
  required?: boolean;
  /** Helper/description text below the label */
  description?: string;
  /** Associate label with input via htmlFor */
  htmlFor?: string;
  /** Horizontal layout (label left, input right) */
  horizontal?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Form field content */
  children: React.ReactNode;
}

export const FormField: React.FC<FormFieldProps> = ({
  label,
  error,
  required = false,
  description,
  htmlFor,
  horizontal = false,
  className,
  children,
}) => {
  const generatedId = useId();
  const fieldId = htmlFor || generatedId;

  if (horizontal) {
    return (
      <div className={clsx('flex items-start gap-4', className)}>
        <div className="w-1/3 pt-2 flex-shrink-0">
          <label
            htmlFor={fieldId}
            className="block text-sm font-medium"
            style={{ color: 'var(--theme-text)' }}
          >
            {label}
            {required && <span className="ml-1" style={{ color: 'var(--theme-error-color)' }}>*</span>}
          </label>
          {description && (
            <p className="text-xs mt-0.5" style={{ color: 'var(--theme-muted-text)' }}>
              {description}
            </p>
          )}
        </div>
        <div className="flex-1 space-y-1">
          {children}
          {error && (
            <p className="text-sm" style={{ color: 'var(--theme-error-color)' }} role="alert">
              {error}
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={clsx('space-y-1', className)}>
      <label
        htmlFor={fieldId}
        className="block text-sm font-medium"
        style={{ color: 'var(--theme-text)' }}
      >
        {label}
        {required && <span className="ml-1" style={{ color: 'var(--theme-error-color)' }}>*</span>}
      </label>
      {description && (
        <p className="text-xs" style={{ color: 'var(--theme-muted-text)' }}>
          {description}
        </p>
      )}
      {children}
      {error && (
        <p className="text-sm" style={{ color: 'var(--theme-error-color)' }} role="alert">
          {error}
        </p>
      )}
    </div>
  );
};

FormField.displayName = 'FormField';